// js/components/ownerNav.js
// Persistent bottom navigation for owner pages.
// Returns { setActive, setUserInfo } so the router can update state on navigation.

import { refreshUI } from "../lib/ui.js";
import { t } from "../lib/i18n.js";
import { BRAND_NAME } from "../lib/config.js";
import { getOwnerPlanState } from "../lib/ownerPlan.js";

const LEFT_HREFS = [
  { href: "/owner", i18nKey: "ownerNav.home", icon: "home" },
  { href: "/owner/shop", i18nKey: "ownerNav.shop", icon: "store" },
];

// Sidebar-only links shown ABOVE the separator (desktop/tablet), hidden on mobile
// Placed after Customers, before the divider
const PRE_SEPARATOR_HREFS = [
  {
    href: "/owner/history",
    i18nKey: "ownerNav.history",
    icon: "history",
  },
  {
    href: "/owner/analytics",
    i18nKey: "ownerNav.analytics",
    icon: "bar-chart-2",
  },
];

// Sidebar links (desktop/tablet only — hidden on mobile bottom nav)
// Sequence: Analytics → Settings → Account → Billing → Contact (below the divider)
const SIDEBAR_ONLY_HREFS = [
  { href: "/owner/settings", i18nKey: "ownerNav.settings", icon: "settings-2" },
  { href: "/owner/account", i18nKey: "ownerNav.account", icon: "circle-user" },
  { href: "/owner/billing", i18nKey: "ownerNav.billing", icon: "wallet-cards" },
  {
    href: "/owner/contact",
    i18nKey: "ownerNav.contact",
    icon: "message-circle",
  },
];

// Mobile-only (bottom nav): Customers + Analytics + More button
const MOBILE_RIGHT_HREFS = [
  { href: "/owner/customers", i18nKey: "ownerNav.customers", icon: "users" },
];

// "More" drawer items — shown inside the slide-in drawer on mobile
const DRAWER_LINKS = [
  {
    href: "/owner/history",
    i18nKey: "ownerNav.history",
    icon: "history",
  },
  {
    href: "/owner/analytics",
    i18nKey: "ownerNav.analytics",
    icon: "bar-chart-2",
  },
  { href: "/owner/account", i18nKey: "ownerNav.account", icon: "circle-user" },
  { href: "/owner/billing", i18nKey: "ownerNav.billing", icon: "wallet-cards" },
  { href: "/owner/settings", i18nKey: "ownerNav.settings", icon: "settings-2" },
  {
    href: "/owner/contact",
    i18nKey: "ownerNav.contact",
    icon: "message-circle",
  },
];

// Sidebar-only section — visually separated from the main nav group (desktop/tablet)
const DISPLAY_SECTION_HREFS = [
  { href: "/owner/display", i18nKey: "ownerNav.displayQr", icon: "qr-code" },
];

// Routes where the "More" button shows as active
const MORE_PATHS = [
  "/owner/history",
  "/owner/settings",
  "/owner/account",
  "/owner/billing",
  "/owner/contact",
  "/owner/display",
];

// Paths that belong to the "Shop" nav tab
const SHOP_PATHS = ["/owner/shop", "/owner/services", "/owner/rewards"];

/**
 * Render the owner bottom nav into the given container element.
 * @param {HTMLElement} container  The <nav id="app-nav"> element.
 * @param {{ navigate: Function }} opts
 * @returns {{ setActive: (path: string) => void, setUserInfo: (profile: object) => void }}
 */
export function renderOwnerNav(container, { navigate }) {
  container.className = "bottom-nav bottom-nav--owner";

  container.innerHTML = `
    <div class="bottom-nav__inner">
      <div class="nav-brand">
        <span class="nav-brand__powered">powered by</span>
        <span class="nav-brand__name">${BRAND_NAME}</span>
      </div>

      ${LEFT_HREFS.map((link) => navItem(link)).join("")}

      <div class="bottom-nav__fab-slot" aria-hidden="true"></div>

      ${MOBILE_RIGHT_HREFS.map((link) => navItem(link)).join("")}
      ${PRE_SEPARATOR_HREFS.map((link) => navItem({ ...link, sidebarOnly: true })).join("")}
      <div class="nav-separator" aria-hidden="true"></div>
      ${SIDEBAR_ONLY_HREFS.map((link) => navItem({ ...link, sidebarOnly: true })).join("")}
      <div class="nav-separator" aria-hidden="true"></div>
      ${DISPLAY_SECTION_HREFS.map((link) => navItem({ ...link, sidebarOnly: true })).join("")}

      <button
        class="bottom-nav__link bottom-nav__link--mobile-only"
        id="owner-more-btn"
        type="button"
        aria-label="${t("ownerNav.more")}"
        aria-expanded="false"
        aria-haspopup="true"
      >
        <i data-lucide="ellipsis-vertical" aria-hidden="true"></i>
        <span class="bottom-nav__label" data-i18n="ownerNav.more">${t("ownerNav.more")}</span>
      </button>
    </div>

    <button class="bottom-nav__fab bottom-nav__fab--scan" id="owner-scan-fab" data-i18n-aria="ownerNav.scanAria" aria-label="${t("ownerNav.scanAria")}">
      <i data-lucide="scan-line" aria-hidden="true"></i>
    </button>

    <div class="owner-nav-user" id="owner-nav-user" aria-hidden="true"></div>
  `;

  // ── More drawer — appended to document.body to escape backdrop-filter
  //    stacking context that would otherwise clip position:fixed children ──────
  document.querySelector("#owner-more-drawer-backdrop")?.remove();
  document.querySelector("#owner-more-drawer")?.remove();

  const backdropEl = document.createElement("div");
  backdropEl.id = "owner-more-drawer-backdrop";
  backdropEl.className = "more-drawer__backdrop";
  backdropEl.setAttribute("aria-hidden", "true");

  const drawerEl = document.createElement("div");
  drawerEl.id = "owner-more-drawer";
  drawerEl.className = "more-drawer";
  drawerEl.setAttribute("role", "dialog");
  drawerEl.setAttribute("aria-modal", "true");
  drawerEl.setAttribute("aria-label", t("ownerNav.more"));
  drawerEl.setAttribute("aria-hidden", "true");
  drawerEl.innerHTML = `
    <div class="more-drawer__header">
      <span class="more-drawer__title" data-i18n="ownerNav.more">${t("ownerNav.more")}</span>
      <button class="more-drawer__close" id="more-drawer-close" aria-label="${t("ownerNav.closeMore")}">
        <i data-lucide="x" aria-hidden="true"></i>
      </button>
    </div>
    <nav class="more-drawer__nav">
      ${DRAWER_LINKS.map((link) => drawerItem(link)).join("")}
      <div class="more-drawer__separator" aria-hidden="true"></div>
      ${DISPLAY_SECTION_HREFS.map((link) => drawerItem(link)).join("")}
    </nav>
    <div class="more-drawer__user" id="more-drawer-user"></div>
  `;

  document.body.appendChild(backdropEl);
  document.body.appendChild(drawerEl);

  refreshUI();

  // ── Drawer state ────────────────────────────────────────────────────────────
  const moreBtn = container.querySelector("#owner-more-btn");
  const drawer = drawerEl;
  const backdrop = backdropEl;

  function openDrawer() {
    drawer.classList.add("more-drawer--open");
    drawer.setAttribute("aria-hidden", "false");
    backdrop.classList.add("more-drawer__backdrop--visible");
    backdrop.setAttribute("aria-hidden", "false");
    moreBtn?.setAttribute("aria-expanded", "true");
  }

  function closeDrawer() {
    drawer.classList.remove("more-drawer--open");
    drawer.setAttribute("aria-hidden", "true");
    backdrop.classList.remove("more-drawer__backdrop--visible");
    backdrop.setAttribute("aria-hidden", "true");
    moreBtn?.setAttribute("aria-expanded", "false");
  }

  moreBtn?.addEventListener("click", () => {
    const isOpen = drawer.classList.contains("more-drawer--open");
    isOpen ? closeDrawer() : openDrawer();
  });

  backdrop.addEventListener("click", closeDrawer);

  drawer
    .querySelector("#more-drawer-close")
    ?.addEventListener("click", closeDrawer);

  // Wire drawer items
  drawer.querySelectorAll(".more-drawer__item").forEach((el) => {
    el.addEventListener("click", (e) => {
      e.preventDefault();
      closeDrawer();
      navigate(el.getAttribute("href"));
    });
  });

  // Wire bottom nav links (SPA navigation)
  container.querySelectorAll(".bottom-nav__link[href]").forEach((el) => {
    el.addEventListener("click", (e) => {
      e.preventDefault();
      navigate(el.getAttribute("href"));
    });
  });

  // Scan FAB → navigate to the scan view
  container.querySelector("#owner-scan-fab")?.addEventListener("click", () => {
    navigate("/owner/scan");
  });

  return {
    setUserInfo(profile) {
      if (!profile) return;
      // Sidebar user block (≥768px)
      const userBlock = container.querySelector("#owner-nav-user");
      if (userBlock) {
        userBlock.removeAttribute("aria-hidden");
      }
      // Shared render helper
      const name = profile.name || profile.email || "";
      const initials = name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((w) => w[0]?.toUpperCase() ?? "")
        .join("");
      const { status } = getOwnerPlanState(profile);
      const planLabel =
        status === "premium"
          ? t("pricing.premium.name")
          : t("pricing.free.name");
      const checkoutUrl =
        "/marketing/checkout.html?plan=premium&billing=monthly";
      const upgradeHtml =
        status !== "premium"
          ? `<a href="${checkoutUrl}" class="owner-nav-user__upgrade" data-i18n="ownerNav.upgrade">${t("ownerNav.upgrade")}</a>`
          : "";
      const userHTML = `
        <div class="owner-nav-user__top">
          <div class="owner-nav-user__avatar" aria-hidden="true">${initials}</div>
          <div class="owner-nav-user__info">
            <span class="owner-nav-user__name">${name}</span>
            <div class="owner-nav-user__meta">
              <span class="owner-nav-user__plan owner-nav-user__plan--${status}">${planLabel}</span>
              ${upgradeHtml}
            </div>
          </div>
        </div>
      `;
      if (userBlock) userBlock.innerHTML = userHTML;
      // Drawer user block (mobile)
      const drawerUser = drawerEl.querySelector("#more-drawer-user");
      if (drawerUser) {
        drawerUser.innerHTML = userHTML;
        drawerUser.removeAttribute("aria-hidden");
        refreshUI();
      }
    },

    setActive(path) {
      // Regular nav links
      container.querySelectorAll(".bottom-nav__link[href]").forEach((el) => {
        const href = el.getAttribute("href");
        let active = false;
        if (href === "/owner") {
          active = path === "/owner";
        } else if (href === "/owner/shop") {
          active =
            path === "/owner/shop" ||
            path.startsWith("/owner/shop/") ||
            SHOP_PATHS.some(
              (p) =>
                p !== "/owner/shop" && (path === p || path.startsWith(p + "/")),
            );
        } else {
          active = path === href || path.startsWith(href + "/");
        }
        el.setAttribute("aria-current", active ? "page" : "false");
        el.classList.toggle("bottom-nav__link--active", active);
      });

      // "More" button active state
      const isMoreActive = MORE_PATHS.some(
        (p) => path === p || path.startsWith(p + "/"),
      );
      moreBtn?.classList.toggle("bottom-nav__link--active", isMoreActive);
      moreBtn?.setAttribute("aria-current", isMoreActive ? "page" : "false");

      // Drawer items active state
      drawer.querySelectorAll(".more-drawer__item").forEach((el) => {
        const href = el.getAttribute("href");
        const active = path === href || path.startsWith(href + "/");
        el.classList.toggle("more-drawer__item--active", active);
        el.setAttribute("aria-current", active ? "page" : "false");
      });

      // Highlight scan FAB when on the scan page
      const fab = container.querySelector("#owner-scan-fab");
      fab?.classList.toggle("bottom-nav__fab--active", path === "/owner/scan");

      // Close drawer on navigation
      closeDrawer();
    },
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function navItem({ href, i18nKey, icon, sidebarOnly = false }) {
  const label = t(i18nKey);
  return `
    <a href="${href}"
       class="bottom-nav__link${sidebarOnly ? " bottom-nav__link--sidebar-only" : ""}"
       aria-current="false"
       data-i18n-aria="${i18nKey}"
       aria-label="${label}">
      <i data-lucide="${icon}" aria-hidden="true"></i>
      <span class="bottom-nav__label" data-i18n="${i18nKey}">${label}</span>
    </a>
  `;
}

function drawerItem({ href, i18nKey, icon }) {
  const label = t(i18nKey);
  return `
    <a href="${href}"
       class="more-drawer__item"
       aria-current="false"
       data-i18n-aria="${i18nKey}"
       aria-label="${label}">
      <i data-lucide="${icon}" aria-hidden="true"></i>
      <span data-i18n="${i18nKey}">${label}</span>
    </a>
  `;
}
